import * as React from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

function pad2(n) { return String(n).padStart(2, "0"); }

function parseHHMM(value) {
  if (!value) return { h: null, m: null };
  const [h, m] = String(value).split(":").map(Number);
  return { h: isNaN(h) ? null : h, m: isNaN(m) ? null : m };
}

function format12h(hhmm) {
  const { h, m } = parseHHMM(hhmm);
  if (h == null || m == null) return "";
  const period = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad2(m)} ${period}`;
}

/**
 * Drop-in replacement for `<input type="time">`. Internal value is "HH:MM"
 * (24-hour), but the trigger displays in 12-hour format for readability.
 *
 *   <TimePicker value="09:30" onChange={(s) => setStart(s)} step={15} />
 *
 * `step` controls the minute granularity in the popover (default 15).
 */
export function TimePicker({
  value,
  onChange,
  step = 15,
  className,
  disabled,
  placeholder = "--:--",
  testId,
  align = "start",
  size = "md", // "sm" | "md"
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value || "");

  React.useEffect(() => { setDraft(value || ""); }, [value]);

  const { h: curH, m: curM } = parseHHMM(value);

  const commit = (h, m) => {
    if (h == null || m == null) return;
    const next = `${pad2(h)}:${pad2(m)}`;
    setDraft(next);
    onChange(next);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [];
  for (let m = 0; m < 60; m += step) minutes.push(m);

  const sizeClasses = size === "sm"
    ? "h-7 px-1.5 text-xs gap-1"
    : "h-9 px-3 text-sm gap-2";

  const onInputBlur = () => {
    const m = /^(\d{1,2}):(\d{1,2})$/.exec(draft.trim());
    if (m) {
      const h = Math.max(0, Math.min(23, Number(m[1])));
      const mm = Math.max(0, Math.min(59, Number(m[2])));
      commit(h, mm);
    } else if (/^\d{3,4}$/.test(draft.trim())) {
      const s = draft.trim().padStart(4, "0");
      commit(Number(s.slice(0, 2)), Number(s.slice(2)));
    } else {
      setDraft(value || "");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "inline-flex items-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:border-[#145c8c] focus:outline-none disabled:opacity-50",
            sizeClasses,
            !value && "text-slate-400",
            className,
          )}
        >
          <Clock className={cn("shrink-0 text-slate-400", size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} />
          <span className="timer-font tabular-nums truncate">
            {value ? format12h(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-2" align={align}>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={onInputBlur}
            onKeyDown={(e) => { if (e.key === "Enter") { onInputBlur(); setOpen(false); } }}
            placeholder="HH:MM"
            className="flex-1 timer-font tabular-nums text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:border-[#145c8c] outline-none"
            data-testid={testId ? `${testId}-input` : undefined}
            autoFocus
          />
          <span className="text-[10px] uppercase tracking-widest text-slate-400">24h</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="border border-slate-100 rounded-md overflow-hidden">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center py-1 bg-slate-50">Hour</div>
            <div className="max-h-44 overflow-y-auto">
              {hours.map((h) => {
                const isOn = curH === h;
                return (
                  <button
                    key={h}
                    onClick={() => commit(h, curM ?? 0)}
                    className={cn(
                      "w-full text-center timer-font tabular-nums text-sm py-1 hover:bg-slate-100",
                      isOn && "bg-[#145c8c] text-white hover:bg-[#0b2c47]",
                    )}
                    data-testid={testId ? `${testId}-hour-${h}` : undefined}
                  >
                    {pad2(h)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="border border-slate-100 rounded-md overflow-hidden">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center py-1 bg-slate-50">Min</div>
            <div className="max-h-44 overflow-y-auto">
              {minutes.map((m) => {
                const isOn = curM === m;
                return (
                  <button
                    key={m}
                    onClick={() => commit(curH ?? 0, m)}
                    className={cn(
                      "w-full text-center timer-font tabular-nums text-sm py-1 hover:bg-slate-100",
                      isOn && "bg-[#145c8c] text-white hover:bg-[#0b2c47]",
                    )}
                    data-testid={testId ? `${testId}-minute-${m}` : undefined}
                  >
                    {pad2(m)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
